import { createContext, useContext } from 'react';

export type FormFieldVisibility = 'visible' | 'hidden';

/**
 * Lets a dense layout that already names a control — a property row with its own
 * label column — suppress the label the control's own `FormField` would render,
 * without every intermediate component having to forward a prop.
 *
 * Only the outermost `FormField` under a provider is affected: it renders its
 * label for screen readers alone and restores visible labels for anything nested
 * inside it, so a composite editor keeps naming its own sub-fields.
 */
export const FormFieldLabelVisibilityContext = createContext<FormFieldVisibility>('visible');

export function useFormFieldLabelVisibility(): FormFieldVisibility {
  return useContext(FormFieldLabelVisibilityContext);
}

/**
 * The help-text counterpart: a dense layout such as a filter condition row has
 * no room for a description under every value input. The text stays in the
 * DOM for `aria-describedby`; only its visual rendering is suppressed. Scoped
 * like the label context — the outermost `FormField` alone.
 */
export const FormFieldHelpTextVisibilityContext = createContext<FormFieldVisibility>('visible');

export function useFormFieldHelpTextVisibility(): FormFieldVisibility {
  return useContext(FormFieldHelpTextVisibilityContext);
}
